import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { clsx } from '@wego/alexis/helpers/clsx';

import { handleCopyToClipboard } from '@helpers/utils';

import CopyIcon from '@icons/copy.svg';
import PercentageOutlinedIcon from '@icons/promo_percent_outlined.svg';

import { getTranslationsState } from '@redux/selectors';

import styles from './FarePromos.module.scss';

export interface FarePromosProps {
  promos: FlightPromo[];
  isWegoFare?: boolean;
  isLoadingFares?: boolean;
}

const FarePromos: React.FC<FarePromosProps> = ({ promos, isWegoFare, isLoadingFares }) => {
  const [isPromoExpanded, setIsPromoExpanded] = useState<Array<boolean>>([]);
  const [isShowingCopiedState, setIsShowingCopiedState] = useState(false);
  const translations = useSelector(getTranslationsState);

  useEffect(() => {
    setIsPromoExpanded(promos.map(() => false));
  }, [promos]);

  const expandCollapsePromoDetails = useCallback(
    (promoIndex: number): void => {
      if (!isLoadingFares) {
        setIsPromoExpanded((prev) => {
          const newStatus = [...prev];
          newStatus[promoIndex] = !newStatus[promoIndex];
          return newStatus;
        });
      }
    },
    [isLoadingFares],
  );

  const handlePromoClick = useCallback((promoCode: string) => {
    handleCopyToClipboard(promoCode);
    setIsShowingCopiedState(true);
    setTimeout(() => {
      setIsShowingCopiedState(false);
    }, 2000);
  }, []);

  const renderCopyCodeSection = useCallback(
    (promosCode: string) => {
      if (isShowingCopiedState) {
        return (
          <>
            <CopyIcon className={styles.copyIcon} />
            {translations.copied}!
          </>
        );
      }

      return (
        <>
          <div className={styles.promoCode}>{promosCode}</div>

          <div className={styles.copyButton}>
            <CopyIcon className={styles.copyIcon} />
          </div>

          <div className={styles.clickToCopy}>{translations.click_to_copy}</div>
        </>
      );
    },
    [isShowingCopiedState, translations.click_to_copy, translations.copied],
  );

  if (promos.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      {promos.map((promo, index) => {
        return (
          <div
            key={promo.code}
            className={clsx(styles.promoContainer, isWegoFare && styles.isWegoPromo)}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.promo}>
              <div
                className={clsx(styles.promoShortDesc, isLoadingFares && styles.loadingFares)}
                onClick={() => expandCollapsePromoDetails(index)}
              >
                <PercentageOutlinedIcon className={styles.percentIcon} />

                <div className={styles.discount}>{promo.shortMessage}</div>

                {promo.description ? (
                  <div
                    className={clsx(
                      styles.chevron,
                      isPromoExpanded && isPromoExpanded[index] ? styles.top : styles.bottom,
                    )}
                  ></div>
                ) : null}
              </div>
              {promo.code && (
                <div
                  className={clsx(styles.copyCodeContainer, isShowingCopiedState && styles.copied)}
                  onClick={() => handlePromoClick(promo.code)}
                >
                  {renderCopyCodeSection(promo.code)}
                </div>
              )}
            </div>

            {promo.description && isPromoExpanded && isPromoExpanded[index] ? (
              <>
                <div className={styles.horizontalLine}></div>

                <div
                  className={styles.promoDescription}
                  dangerouslySetInnerHTML={{ __html: promo.description }}
                ></div>
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default FarePromos;
